import React from 'react';
import {
  Box,
  Button,
  Typography,
  Popover,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { Features } from '@gql/base-types';
import { useGetPortalFeaturesQuery } from '../queries/getPortalFeatures.query.generated';

interface BulkActionBarProps {
  selectedCount: number;
  onGrantFeature: (featureId: number) => void;
  onRevokeFeature: (featureId: number) => void;
  onDeactivate: () => void;
}

const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  onGrantFeature,
  onRevokeFeature,
  onDeactivate,
}) => {
  const [grantAnchor, setGrantAnchor] = React.useState<HTMLElement | null>(null);
  const [revokeAnchor, setRevokeAnchor] = React.useState<HTMLElement | null>(null);

  const { data } = useGetPortalFeaturesQuery();
  const features = (data?.features ?? []) as Features[];

  if (selectedCount === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        bgcolor: '#202430',
        color: '#fff',
        borderRadius: 2,
        px: 3,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        boxShadow: '0px 16px 48px rgba(0,0,0,0.14), 0px 8px 16px rgba(0,0,0,0.06)',
        zIndex: 1300,
      }}
    >
      <Typography variant="body2" fontWeight={600}>
        {selectedCount} selected
      </Typography>

      <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

      <Button
        variant="outlined"
        size="small"
        onClick={e => setGrantAnchor(e.currentTarget)}
        sx={{
          color: '#fff',
          borderColor: 'rgba(255,255,255,0.3)',
          textTransform: 'none',
          '&:hover': { borderColor: 'rgba(255,255,255,0.5)' },
        }}
      >
        Grant Feature
      </Button>

      <Popover
        open={Boolean(grantAnchor)}
        anchorEl={grantAnchor}
        onClose={() => setGrantAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { p: 2, minWidth: 200 } } }}
      >
        <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
          Grant feature to {selectedCount} user{selectedCount > 1 ? 's' : ''}
        </Typography>
        {features.map(f => (
          <FormControlLabel
            key={f.id}
            control={
              <Checkbox
                size="small"
                onChange={() => {
                  onGrantFeature(f.id);
                  setGrantAnchor(null);
                }}
              />
            }
            label={<Typography variant="body2">{f.label}</Typography>}
            sx={{ display: 'block' }}
          />
        ))}
      </Popover>

      <Button
        variant="outlined"
        size="small"
        onClick={e => setRevokeAnchor(e.currentTarget)}
        sx={{
          color: '#fff',
          borderColor: 'rgba(255,255,255,0.3)',
          textTransform: 'none',
          '&:hover': { borderColor: 'rgba(255,255,255,0.5)' },
        }}
      >
        Revoke Feature
      </Button>

      <Popover
        open={Boolean(revokeAnchor)}
        anchorEl={revokeAnchor}
        onClose={() => setRevokeAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { p: 2, minWidth: 200 } } }}
      >
        <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
          Revoke feature from {selectedCount} user{selectedCount > 1 ? 's' : ''}
        </Typography>
        {features.map(f => (
          <FormControlLabel
            key={f.id}
            control={
              <Checkbox
                size="small"
                onChange={() => {
                  onRevokeFeature(f.id);
                  setRevokeAnchor(null);
                }}
              />
            }
            label={<Typography variant="body2">{f.label}</Typography>}
            sx={{ display: 'block' }}
          />
        ))}
      </Popover>

      <Button
        variant="outlined"
        size="small"
        color="error"
        onClick={onDeactivate}
        sx={{
          ml: 1,
          borderColor: 'rgba(255,100,100,0.5)',
          textTransform: 'none',
          '&:hover': { borderColor: 'rgba(255,100,100,0.8)' },
        }}
      >
        Deactivate
      </Button>
    </Box>
  );
};

export default BulkActionBar;
